import type { Issue } from '../types/issue';
import type { AppMeta } from '../api/client';
import type { ReleaseCal } from './releases';
import type { CapMap } from './capacity';
import type { InitialPlanMap } from './columns';

// Backup-Snapshot — komplettes State-Bild für JSON-Export.
// Version wird beim Restore geprüft (Forward-Compat: v1-Backups aus der
// alten App werden ebenfalls akzeptiert, sofern die Top-Level-Keys passen).
export type Backup = {
  version: 'v2';
  exportedAt: string;       // ISO-String
  meta: AppMeta;
  rows: Issue[];
  releaseCal: Record<string, ReleaseCal>;
  capacityByTeam: CapMap;
  initialPlan: InitialPlanMap;
  spToPtFactor: number;
};

// Schreibt ein JS-Objekt als formatiertes JSON-File herunter.
// Filename ist "schuecal-backup-YYYY-MM-DD-HHMM.json".
export function downloadBackup(b: Backup): void {
  const ts = new Date(b.exportedAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = `${ts.getFullYear()}-${pad(ts.getMonth() + 1)}-${pad(ts.getDate())}`;
  const hm  = `${pad(ts.getHours())}${pad(ts.getMinutes())}`;
  const name = `schuecal-backup-${ymd}-${hm}.json`;

  const blob = new Blob([JSON.stringify(b, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Parsing + Validierung eines Backup-JSON. Liefert (Backup, warnings) oder
// wirft einen Error mit lesbarer Nachricht bei strukturellen Problemen.
export type ParseResult = { backup: Backup; warnings: string[] };

export async function parseBackupFile(file: File): Promise<ParseResult> {
  const text = await file.text();
  let raw: unknown;
  try { raw = JSON.parse(text); }
  catch (e) { throw new Error(`Datei ist kein gültiges JSON: ${(e as Error).message}`); }

  if (!raw || typeof raw !== 'object') throw new Error('Erwartet ein JSON-Objekt auf oberster Ebene.');
  const o = raw as Record<string, unknown>;

  const warnings: string[] = [];
  const version = o.version === 'v2' || o.version === 'v1' ? o.version : null;
  if (!version) warnings.push(`Unbekannte Version "${o.version}" — wird trotzdem versucht zu laden.`);

  if (!Array.isArray(o.rows)) throw new Error('Feld "rows" fehlt oder ist kein Array.');

  const backup: Backup = {
    version: 'v2',
    exportedAt: typeof o.exportedAt === 'string' ? o.exportedAt : new Date().toISOString(),
    meta:           (o.meta           ?? {}) as AppMeta,
    rows:            o.rows as Issue[],
    releaseCal:     (o.releaseCal     ?? {}) as Record<string, ReleaseCal>,
    capacityByTeam: (o.capacityByTeam ?? {}) as CapMap,
    initialPlan:    (o.initialPlan    ?? {}) as InitialPlanMap,
    spToPtFactor:    typeof o.spToPtFactor === 'number' && o.spToPtFactor > 0 ? o.spToPtFactor : 1.0,
  };

  // Plausibilitäts-Checks → nur Warnungen, kein Abbruch
  if (!backup.rows.length) warnings.push('Backup enthält keine Issues (rows = []).');
  if (!Object.keys(backup.releaseCal).length) warnings.push('Backup enthält keinen Release-Kalender.');
  if (!Object.keys(backup.capacityByTeam).length) warnings.push('Backup enthält keine Kapazitäten.');

  return { backup, warnings };
}
